import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronRight, Stethoscope } from 'lucide-react';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@roger/ui-components';

import { getChatDirectory, type DirectoryRow } from '../../services/api';
import { useStore } from '../../store';
import { peopleCount, sameEntry, type EntryRef } from './memberEntries';
import { usePickableRoles, usePickableSources } from './useGroupAdminQueries';

const DEBOUNCE_MS = 200;
const DIRECTORY_LIMIT = 50;

type Person = { id: string; label: string };

type SourceOption = {
  key: string;
  label: string;
  /** The row text when it differs from the label, for a pair listed under
   *  its clinic. */
  display?: string;
  count: number;
  entry: EntryRef;
};

type MemberPickerProps = {
  selected: EntryRef[];
  onPick: (entry: EntryRef, label: string) => void;
  /** Blocks picking a result, not the input: while a save or its refetch is
   *  in flight a pick would build its body from stale entries, but the admin
   *  can keep typing the next name. */
  pickDisabled?: boolean;
};

function SourceItem({
  option,
  icon,
  disabled,
  onPick,
  trailing,
  reserveTrailing,
  className,
}: {
  option: SourceOption;
  icon: ReactNode;
  disabled?: boolean;
  onPick: (entry: EntryRef, label: string) => void;
  trailing?: ReactNode;
  /** Keep a chevron-wide space when this row has no chevron, so its count
   *  lines up with the clinic rows that have one. */
  reserveTrailing?: boolean;
  className?: string;
}) {
  return (
    <CommandItem
      value={option.key}
      disabled={disabled}
      onSelect={() => onPick(option.entry, option.label)}
      className={className}
    >
      {icon}
      <span className='truncate'>{option.display ?? option.label}</span>
      <span className='ml-auto whitespace-nowrap pl-2 text-xs text-muted-foreground'>
        {peopleCount(option.count)}
      </span>
      {trailing ||
        (reserveTrailing && (
          <span
            data-chevron-space
            aria-hidden
            className='-mr-1 ml-1 w-7 shrink-0'
          />
        ))}
    </CommandItem>
  );
}

/** A pair's label is "{clinic} · {discipline}"; a discipline name has no
 *  " · ", so the last part is the discipline even when the clinic has one. */
function splitPairLabel(label: string): { clinic: string; discipline: string } {
  const cut = label.lastIndexOf(' · ');
  return { clinic: label.slice(0, cut), discipline: label.slice(cut + 3) };
}

export function MemberPicker({
  selected,
  onPick,
  pickDisabled,
}: MemberPickerProps) {
  const user = useStore(state => state.user);
  const { data: roles = [] } = usePickableRoles();
  const { data: sources } = usePickableSources();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const searchText = debouncedQuery.trim();
  const directory = useQuery({
    queryKey: ['groups', 'picker-directory', searchText],
    enabled: searchText !== '',
    queryFn: async () => {
      const { data } = await getChatDirectory({
        query: { q: searchText, limit: DIRECTORY_LIMIT },
        throwOnError: true,
      });
      return (data ?? []) as DirectoryRow[];
    },
  });

  const typed = query.trim().toLowerCase();
  const words = typed.split(/\s+/).filter(Boolean);
  // Each typed word on its own, so "north pt" finds "North · Physical therapy"
  // only when both words are in the label.
  const matches = (label: string) => {
    const lower = label.toLowerCase();
    return words.every(word => lower.includes(word));
  };
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [highlighted, setHighlighted] = useState('');
  const toggleClinic = (clinicId: string, open: boolean) =>
    setExpanded(current => {
      const next = new Set(current);
      if (open) next.add(clinicId);
      else next.delete(clinicId);
      return next;
    });
  const isSelected = (kind: 'user' | 'role', id: string) =>
    selected.some(entry => sameEntry(entry, { kind, id }));

  const visibleRoles = roles.filter(
    role => !isSelected('role', role.id) && matches(role.name)
  );

  const sourceOptions = (options: SourceOption[]) =>
    options.filter(
      option =>
        !selected.some(entry => sameEntry(entry, option.entry)) &&
        // The discipline code too, so "pt" finds Physical therapy.
        matches(
          option.entry.kind === 'source' && option.entry.discipline
            ? `${option.label} ${option.entry.discipline}`
            : option.label
        )
    );
  const visibleClinics = sourceOptions(
    (sources?.clinics ?? []).map(clinic => ({
      key: `clinic:${clinic.id}`,
      label: clinic.name,
      count: clinic.holder_count,
      entry: { kind: 'source', clinic_id: clinic.id },
    }))
  );
  const visibleDisciplines = sourceOptions(
    (sources?.disciplines ?? []).map(item => ({
      key: `discipline:${item.discipline}`,
      label: item.name,
      count: item.holder_count,
      entry: { kind: 'source', discipline: item.discipline },
    }))
  );
  const pairOptions = sourceOptions(
    (sources?.pairs ?? []).map(pair => ({
      key: `pair:${pair.clinic_id}:${pair.discipline}`,
      label: pair.name,
      count: pair.holder_count,
      entry: {
        kind: 'source',
        clinic_id: pair.clinic_id,
        discipline: pair.discipline,
      },
    }))
  );
  // Pairs are many (clinics times disciplines). Without a query they list
  // under a clinic the admin expands; a query lists the matching ones flat.
  const pairsByClinic = new Map<string, SourceOption[]>();
  for (const option of pairOptions) {
    if (option.entry.kind !== 'source' || !option.entry.clinic_id) continue;
    const list = pairsByClinic.get(option.entry.clinic_id) ?? [];
    list.push(option);
    pairsByClinic.set(option.entry.clinic_id, list);
  }
  const visiblePairs = typed === '' ? [] : pairOptions;

  // Right and Left on the highlighted clinic (or one of its pairs) open and
  // close its discipline list, so the keyboard reaches every pair too.
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (typed !== '') return;
    const [kind, clinicId] = highlighted.split(':');
    if (!clinicId || !pairsByClinic.has(clinicId)) return;
    if (event.key === 'ArrowRight' && kind === 'clinic') {
      event.preventDefault();
      toggleClinic(clinicId, true);
    } else if (
      event.key === 'ArrowLeft' &&
      (kind === 'clinic' || kind === 'pair')
    ) {
      event.preventDefault();
      toggleClinic(clinicId, false);
      setHighlighted(`clinic:${clinicId}`);
    }
  };

  const people: Person[] = [];
  if (typed !== '') {
    for (const row of directory.data ?? []) {
      if (row.type === 'user') people.push({ id: row.id, label: row.label });
    }
    // The directory excludes the caller, and an administrator must be able to
    // add themselves.
    if (
      user &&
      user.name.toLowerCase().includes(typed) &&
      !people.some(person => person.id === user.id)
    ) {
      people.push({ id: user.id, label: user.name });
    }
  }
  const visiblePeople = people.filter(person => !isSelected('user', person.id));

  const settled =
    query.trim() === searchText && !directory.isFetching && directory.isFetched;

  // The highlight is controlled (the arrow keys read it), so cmdk does not move
  // it to the first row by itself when the highlighted row leaves the list or
  // a pick clears it. Do that here after every render.
  useLayoutEffect(() => {
    const items = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[cmdk-item]') ?? []
    );
    if (items.length === 0) return;
    if (items.some(item => item.dataset.value === highlighted)) return;
    const first = items[0].dataset.value;
    if (first) setHighlighted(first);
  });

  const pick = (entry: EntryRef, label: string) => {
    if (pickDisabled) return;
    onPick(entry, label);
    setQuery('');
    // Otherwise the highlight stays on the picked row's value and the list
    // reopens partway down.
    setHighlighted('');
    // The highlight goes back to the first row; show it.
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  return (
    <Command
      shouldFilter={false}
      value={highlighted}
      onValueChange={setHighlighted}
      className='h-auto overflow-visible'
    >
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} onPointerDown={() => setOpen(true)}>
            <CommandInput
              placeholder='Add people, roles, clinics or disciplines — type a name'
              value={query}
              onValueChange={value => {
                setQuery(value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={onInputKeyDown}
            />
          </div>
        </PopoverAnchor>
        {/* Above the input, never flipped below: the picker sits over the
            dialog and drawer buttons, and a list below would cover them. */}
        <PopoverContent
          align='start'
          side='top'
          avoidCollisions={false}
          className='w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0'
          onOpenAutoFocus={event => event.preventDefault()}
          onInteractOutside={event => {
            if (anchorRef.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
        >
          <CommandList
            ref={listRef}
            className='max-h-[min(18rem,calc(var(--radix-popover-content-available-height)-2px))] overscroll-contain'
          >
            {visibleRoles.length > 0 && (
              <CommandGroup heading='Roles'>
                {visibleRoles.map(role => (
                  <CommandItem
                    key={`role:${role.id}`}
                    value={`role:${role.id}`}
                    disabled={pickDisabled}
                    onSelect={() =>
                      pick({ kind: 'role', id: role.id }, role.name)
                    }
                  >
                    <span className='truncate'>{role.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {visibleClinics.length > 0 && (
              <CommandGroup heading='Clinics'>
                {visibleClinics.map(option => {
                  const clinicId =
                    option.entry.kind === 'source'
                      ? (option.entry.clinic_id ?? '')
                      : '';
                  const pairs =
                    typed === '' ? (pairsByClinic.get(clinicId) ?? []) : [];
                  const isOpen = expanded.has(clinicId);
                  return (
                    <Fragment key={option.key}>
                      <SourceItem
                        option={option}
                        icon={<Building2 className='mr-2 h-4 w-4 shrink-0' />}
                        disabled={pickDisabled}
                        onPick={pick}
                        reserveTrailing={typed === ''}
                        trailing={
                          pairs.length > 0 && (
                            <button
                              type='button'
                              tabIndex={-1}
                              aria-label={`Disciplines at ${splitPairLabel(pairs[0].label).clinic}`}
                              aria-expanded={isOpen}
                              className='-my-1 -mr-1 ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground'
                              onPointerDown={event => event.preventDefault()}
                              onClick={event => {
                                // The row behind picks the clinic; this only
                                // opens or closes its disciplines.
                                event.stopPropagation();
                                toggleClinic(clinicId, !isOpen);
                              }}
                            >
                              <ChevronRight
                                className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                              />
                            </button>
                          )
                        }
                      />
                      {isOpen &&
                        pairs.map(pair => (
                          <SourceItem
                            key={pair.key}
                            option={{
                              ...pair,
                              display: splitPairLabel(pair.label).discipline,
                            }}
                            icon={
                              <Stethoscope className='mr-2 h-4 w-4 shrink-0' />
                            }
                            disabled={pickDisabled}
                            onPick={pick}
                            reserveTrailing
                            className='pl-8'
                          />
                        ))}
                    </Fragment>
                  );
                })}
              </CommandGroup>
            )}
            {visibleDisciplines.length > 0 && (
              <CommandGroup heading='Disciplines'>
                {visibleDisciplines.map(option => (
                  <SourceItem
                    key={option.key}
                    option={option}
                    icon={<Stethoscope className='mr-2 h-4 w-4 shrink-0' />}
                    disabled={pickDisabled}
                    onPick={pick}
                    reserveTrailing
                  />
                ))}
              </CommandGroup>
            )}
            {visiblePairs.length > 0 && (
              <CommandGroup heading='Clinic and discipline'>
                {visiblePairs.map(option => (
                  <SourceItem
                    key={option.key}
                    option={option}
                    icon={<Building2 className='mr-2 h-4 w-4 shrink-0' />}
                    disabled={pickDisabled}
                    onPick={pick}
                  />
                ))}
              </CommandGroup>
            )}
            {typed === '' ? (
              <p className='px-3 py-2 text-sm text-muted-foreground'>
                Type a name to search your colleagues
              </p>
            ) : visiblePeople.length > 0 ? (
              <CommandGroup heading='People'>
                {visiblePeople.map(person => (
                  <CommandItem
                    key={`user:${person.id}`}
                    value={`user:${person.id}`}
                    disabled={pickDisabled}
                    onSelect={() =>
                      pick({ kind: 'user', id: person.id }, person.label)
                    }
                  >
                    <span className='truncate'>{person.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : settled &&
              visibleRoles.length +
                visibleClinics.length +
                visibleDisciplines.length +
                visiblePairs.length ===
                0 ? (
              <p className='px-3 py-2 text-sm text-muted-foreground'>
                {`Nothing matches "${query.trim()}"`}
              </p>
            ) : null}
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
}
